import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { RootCloseWrapper } from "@lana-commerce/core/react/components/RootCloseWrapper";

import { Badge } from "./Badge";
import { Close } from "./icons/Close";
import { LinkSpec } from "./linkSpec";
import { LocaleLink } from "./api/LocaleLink";
import { Menu } from "./icons/Menu";
import { UniversalLinkWrapper } from "./universalLink";

interface HeaderMenuItem {
  id: string;
  label: () => JSX.Element | string;
  onClick?: () => void;
}

interface HeaderIconButton {
  icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element;
  link: LinkSpec;
  ref?: (v: HTMLElement | null) => void;
  count?: number;
  invisibleOnMobile?: boolean;
}

interface HeaderProps {
  menuItems: HeaderMenuItem[];
  menuActive?: string;
  mobileMenu?: {
    onClick: () => void;
    active: boolean;
    rootCloseWrapperTag: string;
  };
  title: string;
  iconButtons: HeaderIconButton[];
}

export const Header = React.forwardRef<HTMLDivElement, HeaderProps>((props, ref) => {
  const [hl, setHL] = useState<{ x: number; w: number } | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    if (!props.menuActive) {
      setHL(undefined);
      return;
    }
    const elLeft = el.getBoundingClientRect().left;
    const idx = props.menuItems.findIndex((item) => item.id === props.menuActive);
    if (idx === -1) {
      setHL(undefined);
      return;
    }
    const c = el.children[idx];
    if (!c) {
      setHL(undefined);
      return;
    }
    const r = c.getBoundingClientRect();
    setHL({ x: r.left - elLeft, w: r.width });
  }, [props.menuItems, props.menuActive]);
  const mm = props.mobileMenu;
  return (
    <div
      ref={ref}
      className="relative flex px-4 tablet:px-12 items-center justify-between h-12 tablet:h-20 drop-shadow-only-1"
    >
      <RootCloseWrapper tag={mm?.rootCloseWrapperTag} onRootClose={mm?.onClick || (() => {})} disabled={!mm?.active}>
        <div className="hidden relative tablet:flex gap-6 h-full" ref={menuRef}>
          {props.menuItems.map((item) => (
            <div key={item.id} onClick={item.onClick} className="cursor-pointer select-none h-full flex items-center">
              {item.label()}
            </div>
          ))}
          {props.menuActive && hl ? (
            <div
              className="transition-[left,width] absolute -bottom-0.5 h-0.5 bg-black"
              style={{ left: hl.x, width: hl.w }}
            />
          ) : null}
        </div>
      </RootCloseWrapper>
      {mm ? (
        <RootCloseWrapper tag={mm.rootCloseWrapperTag} onRootClose={mm.onClick} disabled={!mm.active}>
          <div className="flex tablet:hidden h-full items-center cursor-pointer select-none" onClick={mm.onClick}>
            {mm.active ? <Close className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </div>
        </RootCloseWrapper>
      ) : (
        <div />
      )}
      <div className="flex gap-2 tablet:gap-6">
        {props.iconButtons.map((b, idx) => (
          <UniversalLinkWrapper
            ref={b.ref}
            key={idx}
            spec={b.link}
            className={classnames("relative cursor-pointer select-none", {
              "hidden tablet:block": b.invisibleOnMobile,
            })}
          >
            {b.icon({ className: "w-4 h-4 tablet:w-6 tablet:h-6" })}
            {b.count !== undefined ? (
              <Badge className="absolute -top-2 -right-2 pointer-events-none" style="round" value={`${b.count}`} />
            ) : null}
          </UniversalLinkWrapper>
        ))}
      </div>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center font-semibold text-center text-lg tablet:text-2xl text-black">
        <span className="pointer-events-auto">
          <LocaleLink to="/">{props.title}</LocaleLink>
        </span>
      </div>
    </div>
  );
});
